import { createAuthedAxiosClient } from '../../api-client/api-client';

/**
 * CENTERLINE AI, the Query Tool's chat (docs/query-tool/token-contract.md).
 *
 * These endpoints are not in the generated client yet, so this calls them directly through the
 * shared authenticated axios client. The types mirror tsoa-src/query-tool/query-tool-models.ts.
 */

export interface IQueryToolStatusResponse {
  available: boolean;
}

export interface IQueryToolInterfaceTokenResponse {
  token: string;
  expiresAt: string;
  /** Seconds until it expires, counted from receipt (the browser's clock isn't trusted). */
  expiresIn: number;
}

export interface IQueryToolLiveProject {
  id: string;
  name: string;
}

const PATH = '/query-tool';

export const getQueryToolStatus = async (projectId: string): Promise<IQueryToolStatusResponse> => {
  const res = await createAuthedAxiosClient().get<IQueryToolStatusResponse>(`${PATH}/status`, {
    params: { projectId },
  });
  return res.data;
};

/** A 15-minute bearer token for the Query Tool's API, naming only `projectId`. */
export const getQueryToolInterfaceToken = async (
  projectId: string,
): Promise<IQueryToolInterfaceTokenResponse> => {
  const res = await createAuthedAxiosClient().post<IQueryToolInterfaceTokenResponse>(
    `${PATH}/interface-token`,
    { projectId },
  );
  return res.data;
};

/** The live projects the caller may ask CENTERLINE AI about. */
export const getQueryToolProjects = async (): Promise<IQueryToolLiveProject[]> => {
  const res = await createAuthedAxiosClient().get<{ projects: IQueryToolLiveProject[] }>(
    `${PATH}/projects`,
  );
  return res.data.projects;
};

/**
 * Whether a subscriber's security groups show the CENTERLINE AI setting to the calling user: the
 * subscriber is entitled and the caller administers it (and, during the production test, is a site
 * admin).
 */
export const getQueryToolSubscriberAvailability = async (
  subscriberId: string,
): Promise<boolean> => {
  const res = await createAuthedAxiosClient().get<{ available: boolean }>(
    `${PATH}/subscribers/${subscriberId}/availability`,
  );
  return res.data.available;
};

/* Site admins' entitlement controls (tsoa-src/query-tool/query-tool-admin-service.ts), used by the
   Edit Subscriber dialog. */

export interface IQueryToolEntitlements {
  subscriberIds: string[];
}

export interface IQueryToolEntitlementResponse {
  enabled: boolean;
  projectsSwitchedOff: number;
}

export const getQueryToolEntitlements = async (): Promise<IQueryToolEntitlements> => {
  const res = await createAuthedAxiosClient().get<IQueryToolEntitlements>(
    `${PATH}/admin/entitlements`,
  );
  return res.data;
};

export const setQueryToolEntitlement = async (
  subscriberId: string,
  enabled: boolean,
): Promise<IQueryToolEntitlementResponse> => {
  const res = await createAuthedAxiosClient().put<IQueryToolEntitlementResponse>(
    `${PATH}/admin/subscribers/${subscriberId}/entitlement`,
    { enabled },
  );
  return res.data;
};

/* The site admins' CENTERLINE AI page (centerline-ai/admin/CenterlineAiAdminPage.tsx). */

/**
 * A project's two CENTERLINE AI settings, each on or off: `ingest`, the Query Tool ingests its
 * files and emails (new ones within about a minute); `chat`, it is available in the chat.
 */
export type QueryToolSetting = 'ingest' | 'chat';

export type QueryToolProjectSettings = Record<QueryToolSetting, boolean>;

export interface IQueryToolAdminSubscriber {
  id: string;
  name: string;
  entitled: boolean;
  /** Its projects that aren't deleted, then how many of those have Ingestion and Chat on. */
  projects: number;
  ingesting: number;
  chat: number;
}

export interface IQueryToolAdminProject extends QueryToolProjectSettings {
  id: string;
  name: string;
  architectProjectNumber?: string | null;
  isArchived: boolean;
  isHidden: boolean;
}

export interface IQueryToolAdminProjects {
  entitled: boolean;
  projects: IQueryToolAdminProject[];
}

export interface IQueryToolAdminSubscribers {
  /** CENTERLINE AI's global switch: off, nobody can use it and the Query Tool reads nothing. */
  enabled: boolean;
  subscribers: IQueryToolAdminSubscriber[];
}

export const getQueryToolAdminSubscribers = async (): Promise<IQueryToolAdminSubscribers> => {
  const res = await createAuthedAxiosClient().get<IQueryToolAdminSubscribers>(
    `${PATH}/admin/subscribers`,
  );
  return res.data;
};

export const setQueryToolSwitch = async (enabled: boolean): Promise<{ enabled: boolean }> => {
  const res = await createAuthedAxiosClient().put<{ enabled: boolean }>(`${PATH}/admin/switch`, {
    enabled,
  });
  return res.data;
};

export const getQueryToolSubscriberProjects = async (
  subscriberId: string,
): Promise<IQueryToolAdminProjects> => {
  const res = await createAuthedAxiosClient().get<IQueryToolAdminProjects>(
    `${PATH}/admin/subscribers/${subscriberId}/projects`,
  );
  return res.data;
};

/** Changes the settings given; one left out stays as it is. */
export const setQueryToolProjectSettings = async (
  projectId: string,
  settings: Partial<QueryToolProjectSettings>,
): Promise<QueryToolProjectSettings> => {
  const res = await createAuthedAxiosClient().put<QueryToolProjectSettings>(
    `${PATH}/admin/projects/${projectId}/settings`,
    settings,
  );
  return res.data;
};
